"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ConfigurationData = void 0;
const utils_1 = require("../utils/utils");
/**
 * Wrapper class to wrap around Configuration Data returned from a query.
 */
class ConfigurationData {
    constructor(config) {
        this._configs = !(0, utils_1.isNullOrUndefined)(config) ? config : new Map();
    }
    /**
     * Extracts the requested configuration field value in String format.
     * Any configuration that is not stored in String format will be converted to a String.
     *
     * @param configName    name of the config field to extract
     *
     * @return Nullable configuration value,
     *         null if configName doesn't exist
     */
    getStringValue(configName) {
        const val = this._configs.get(configName);
        return !(0, utils_1.isNullOrUndefined)(val) ? String(val) : null;
    }
    /**
     * Extracts the requested configuration field value in number format.
     * The configuration can be stored in either number or String format.
     *
     * @param configName    name of the config field to extract
     *
     * @return Nullable configuration value
     *         null if configName doesn't exist
     */
    getNumberValue(configName) {
        let val = this._configs.get(configName);
        if ((0, utils_1.isNullOrUndefined)(val)) {
            return null;
        }
        val = Number(val);
        if (Number.isNaN(val)) {
            throw new Error(`The value of ${configName} cannot be converted to a number`);
        }
        return val;
    }
    /**
     * Extracts the requested configuration field value in boolean format.
     * The configuration can be stored in either boolean or String format.
     *
     * @param configName    name of the config field to extract
     *
     * @return Nullable configuration value
     *         null if configName doesn't exist
     */
    getBooleanValue(configName) {
        const val = this._configs.get(configName);
        return !(0, utils_1.isNullOrUndefined)(val) ? 'true' === String(val).toLowerCase() : null;
    }
    /**
     * Extracts the requested configuration field value in enum format.
     *
     * @param enumType  the enum type that the config value belongs to
     * @param configName    name of the config field to extract
     *
     * @return Nullable configuration value
     *         null if configName doesn't exist
     */
    getEnumValue(enumType, configName) {
        const val = this._configs.get(configName);
        if ((0, utils_1.isNullOrUndefined)(val)) {
            return null;
        }
        let keys = Object.keys(enumType).filter(x => enumType[x] == val);
        return keys.length > 0 ? enumType[keys[0]] : null;
    }
    /**
     * Determines whether the Configuration Data stores a primitive or complex data type.
     * ConfigurationData will be considered as Complex if it contains more than 1 data item.
     *
     * @return true if primitive, false otherwise.
     */
    isPrimitiveData() {
        return this._configs.size <= 1;
    }
    /**
     * Retrieves the Config name of the primitive data of the ConfigurationData object.
     * If the object contains a complex Data Type instead undefined will be returned.
     *
     * @return Underlying Primitive Data name, or null if
     *         Data is complex or non existing.
     */
    getPrimitiveDataConfigName() {
        return this._configs.size == 1 ? this._configs.keys().next().value : null;
    }
    isEmpty() {
        if (this._configs.size === 0) {
            return true;
        }
        for (const val of this._configs.values()) {
            if (!(0, utils_1.isNullOrUndefined)(val)) {
                return false;
            }
        }
        return true;
    }
    merge(data) {
        for (const entry of data._configs.entries()) {
            this._configs.set(entry[0], entry[1]);
        }
        return this;
    }
    toString() {
        let res = '{';
        let configCount = 0;
        for (const entry of this._configs.entries()) {
            res += `${entry[0]}:${entry[1]}`;
            configCount++;
            if (configCount != this._configs.size) {
                res += ',';
            }
        }
        res += '}';
        return res;
    }
}
exports.ConfigurationData = ConfigurationData;
//# sourceMappingURL=configuration-data.js.map